import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Receipt, UserPlus, Search, Loader2, RefreshCw, ExternalLink, Play, X, Link2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import billingService, { xeroInvoiceUrl } from '../../services/billing';
import subscriptionService from '../../services/subscription';
import type { BillingInvoice, BillingManager, BillingOrgSummary, InvoiceFilter, RenewalRunSummary, UpcomingRenewal } from '../../types/billing';
import { BillingGate, ErrorBanner, InvoiceStatusBadge } from '../../components/billing/billingUi';
import { errorText, inputClass, useBillingAccess } from '../../components/billing/billingHelpers';
import { useOrgTimezone, formatDateInTz, formatLocalDateInTz } from '@/utils/timezone';

type Tab = 'organisations' | 'invoices' | 'renewals' | 'team';

const money = (n: unknown) => subscriptionService.formatCurrency(Number(n) || 0);

/**
 * /billing — the platform billing console, for billing_manager and super_admin.
 *
 * Replaces the super-admin subscriptions page: everything commercial about a
 * customer (subscriptions, trials, offers, invoices) lives here, and none of
 * it needs the reach super_admin carries.
 */
export default function BillingConsole() {
  const navigate = useNavigate();
  const { canBill, isSuperAdmin } = useBillingAccess();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'organisations';
  const setTab = (t: Tab) => setParams(t === 'organisations' ? {} : { tab: t }, { replace: true });

  const tabs: { key: Tab; label: string }[] = [
    { key: 'organisations', label: 'Organisations' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'renewals', label: 'Renewals' },
    ...(isSuperAdmin ? [{ key: 'team' as Tab, label: 'Billing team' }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Billing" subtitle="Customers, trials, offers and invoices" icon={<Receipt className="h-6 w-6 text-white" />} />
      <BillingGate canBill={canBill}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex flex-wrap gap-2" role="tablist">
              {tabs.map(t => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                    tab === t.key ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {isSuperAdmin && (
                <Button variant="outline" onClick={() => navigate('/admin/xero')}>
                  <Link2 className="h-4 w-4" />
                  Xero connection
                </Button>
              )}
              <Button variant="primary" onClick={() => navigate('/billing/onboard')}>
                <UserPlus className="h-4 w-4" />
                Onboard customer
              </Button>
            </div>
          </div>

          {tab === 'organisations' && <OrganisationsTab />}
          {tab === 'invoices' && <InvoicesTab />}
          {tab === 'renewals' && <RenewalsTab />}
          {tab === 'team' && isSuperAdmin && <TeamTab />}
        </div>
      </BillingGate>
    </div>
  );
}

function OrganisationsTab() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [search, setSearch] = useState('');
  const [orgs, setOrgs] = useState<BillingOrgSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      billingService.listOrganisations(search.trim() || undefined)
        .then(r => { setOrgs(r ?? []); setError(null); })
        .catch(err => setError(errorText(err, 'Failed to load organisations')));
    }, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div>
      <div className="relative mb-4 max-w-md">
        <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email"
          aria-label="Search organisations"
          className={`${inputClass} pl-9`}
        />
      </div>
      <ErrorBanner message={error} />
      {orgs === null ? (
        <Loader2 className="h-6 w-6 text-primary-600 animate-spin mx-auto my-10" />
      ) : orgs.length === 0 ? (
        <p className="text-neutral-500 text-center py-10">No organisations match.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {orgs.map(o => (
            <button
              key={o.id}
              onClick={() => navigate(`/billing/organisations/${o.id}`)}
              className="text-left bg-white rounded-xl border border-neutral-200 p-4 hover:border-primary-300 hover:shadow-sm transition-all min-w-0"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-neutral-900 break-words min-w-0">{o.name}</h3>
                {o.overdue > 0 && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full whitespace-nowrap">{o.overdue} overdue</span>}
              </div>
              <p className="text-xs text-neutral-500 break-all">{o.billing_email || o.contact_email || 'No billing email'}</p>
              <div className="flex flex-wrap gap-1.5 mt-3 text-xs">
                <span className="bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded-full">{o.active_subscriptions} active</span>
                {o.trials > 0 && (
                  <span className="bg-warning-100 text-warning-800 px-2 py-0.5 rounded-full">
                    {o.trials} trial{o.trials === 1 ? '' : 's'}{o.next_trial_end ? ` · ends ${formatLocalDateInTz(o.next_trial_end, orgTz)}` : ''}
                  </span>
                )}
                {o.active_terms > 0 && <span className="bg-primary-100 text-primary-800 px-2 py-0.5 rounded-full">{o.active_terms} offer{o.active_terms === 1 ? '' : 's'}</span>}
                {o.suspended > 0 && <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full">{o.suspended} suspended</span>}
                {o.needs_attention > 0 && <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">{o.needs_attention} invoice issue{o.needs_attention === 1 ? '' : 's'}</span>}
              </div>
              {o.outstanding > 0 && <p className="text-sm text-neutral-700 mt-2">Outstanding {money(o.outstanding)} inc GST</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const FILTERS: { key: InvoiceFilter; label: string }[] = [
  { key: 'attention', label: 'Needs attention' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'open', label: 'Unpaid' },
  { key: 'paid', label: 'Paid' },
  { key: 'all', label: 'All' },
];

function InvoicesTab() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [filter, setFilter] = useState<InvoiceFilter>('attention');
  const [rows, setRows] = useState<BillingInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | 'sync' | null>(null);

  const load = useCallback(() => {
    setRows(null);
    billingService.listInvoices(filter)
      .then(r => { setRows(r ?? []); setError(null); })
      .catch(err => setError(errorText(err, 'Failed to load invoices')));
  }, [filter]);
  useEffect(load, [load]);

  const retry = async (inv: BillingInvoice) => {
    setBusy(inv.id);
    try { await billingService.retryInvoice(inv.id); load(); }
    catch (err) { setError(errorText(err, 'Retry failed')); }
    finally { setBusy(null); }
  };
  const sync = async () => {
    setBusy('sync');
    try { await billingService.syncInvoiceStatuses(); load(); }
    catch (err) { setError(errorText(err, 'Sync failed')); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-sm border ${filter === f.key ? 'bg-neutral-800 text-white border-neutral-800' : 'bg-white text-neutral-700 border-neutral-200'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={sync} disabled={busy === 'sync'}>
          <RefreshCw className={`h-4 w-4 ${busy === 'sync' ? 'animate-spin' : ''}`} />
          Sync from Xero
        </Button>
      </div>
      {filter === 'attention' && (
        <p className="text-sm text-neutral-600 mb-3">
          Charged here but no invoice reached the customer — Xero was offline or refused it. Retry once the cause is fixed.
        </p>
      )}
      <ErrorBanner message={error} />
      {rows === null ? (
        <Loader2 className="h-6 w-6 text-primary-600 animate-spin mx-auto my-10" />
      ) : rows.length === 0 ? (
        <p className="text-neutral-500 text-center py-10">Nothing here.</p>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Organisation</th>
                  <th className="text-left px-4 py-2 font-medium">Invoice</th>
                  <th className="text-left px-4 py-2 font-medium">Raised</th>
                  <th className="text-left px-4 py-2 font-medium">Due</th>
                  <th className="text-right px-4 py-2 font-medium whitespace-nowrap">Amount ex GST</th>
                  <th className="text-right px-4 py-2 font-medium whitespace-nowrap">Owing inc GST</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map(inv => (
                  <tr key={inv.id}>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <button className="text-primary-700 hover:underline" onClick={() => navigate(`/billing/organisations/${inv.organisation_id}`)}>
                        {inv.organisation_name}
                      </button>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{inv.xero_invoice_number || '—'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDateInTz(inv.created_at, orgTz)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{inv.xero_due_date ? formatLocalDateInTz(inv.xero_due_date, orgTz) : '—'}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{money(inv.amount)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">{inv.xero_amount_due != null ? money(inv.xero_amount_due) : '—'}</td>
                    <td className="px-4 py-2"><InvoiceStatusBadge status={inv.xero_invoice_status} dueDate={inv.xero_due_date} /></td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {inv.xero_invoice_id ? (
                        <a href={xeroInvoiceUrl(inv.xero_invoice_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary-700 hover:underline">
                          Xero <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (inv.xero_invoice_status === 'FAILED' || inv.xero_invoice_status === 'NOT_CONNECTED') && (
                        <Button variant="outline" size="sm" onClick={() => retry(inv)} disabled={busy === inv.id}>
                          {busy === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RenewalsTab() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState<UpcomingRenewal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<RenewalRunSummary | null>(null);

  const load = useCallback(() => {
    setRows(null);
    billingService.upcomingRenewals(days)
      .then(r => { setRows(r ?? []); setError(null); })
      .catch(err => setError(errorText(err, 'Failed to load renewals')));
  }, [days]);
  useEffect(load, [load]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const dueNow = (rows ?? []).filter(r => r.next_billing_date <= todayStr).length;

  const runNow = async () => {
    if (!confirm(`Invoice the ${dueNow} renewal${dueNow === 1 ? '' : 's'} due today or earlier now, instead of waiting for tomorrow's 5 AM run?`)) return;
    setRunning(true);
    try {
      const res = await billingService.runRenewals();
      setSummary(res);
      load();
    } catch (err) {
      setError(errorText(err, 'Renewal run failed'));
    } finally {
      setRunning(false);
    }
  };

  const total = (rows ?? []).reduce((s, r) => s + r.total, 0);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-neutral-600">Next</span>
          {[7, 30, 90].map(n => (
            <button key={n} onClick={() => setDays(n)}
              className={`px-3 py-1.5 rounded-md border ${days === n ? 'bg-neutral-800 text-white border-neutral-800' : 'bg-white text-neutral-700 border-neutral-200'}`}>
              {n} days
            </button>
          ))}
        </div>
        {dueNow > 0 && (
          <Button variant="outline" size="sm" onClick={runNow} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Invoice {dueNow} due now
          </Button>
        )}
      </div>
      <p className="text-sm text-neutral-600 mb-3">
        Priced exactly as the daily 5 AM run will invoice them, offers included, ex GST (Xero adds GST). Only subscriptions with renewal invoicing turned on appear.
      </p>
      {summary && (
        <div className="mb-4 bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-800">
          {summary.invoices} invoice{summary.invoices === 1 ? '' : 's'} raised across {summary.organisations} organisation{summary.organisations === 1 ? '' : 's'}
          {summary.trial_conversions > 0 && `, ${summary.trial_conversions} trial conversion${summary.trial_conversions === 1 ? '' : 's'}`}
          {summary.zero_total > 0 && `, ${summary.zero_total} free period${summary.zero_total === 1 ? '' : 's'} (no invoice)`}
          {summary.failed.length > 0 && `. ${summary.failed.length} failed: ${summary.failed.map(f => f.error).join('; ')}`}
        </div>
      )}
      <ErrorBanner message={error} />
      {rows === null ? (
        <Loader2 className="h-6 w-6 text-primary-600 animate-spin mx-auto my-10" />
      ) : rows.length === 0 ? (
        <p className="text-neutral-500 text-center py-10">Nothing renews in the next {days} days.</p>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100">
          {rows.map(r => (
            <div key={r.subscription_id} className="p-4 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <button className="font-medium text-primary-700 hover:underline text-left break-words" onClick={() => navigate(`/billing/organisations/${r.organisation_id}`)}>
                  {r.organisation_name}
                </button>
                <p className="text-sm text-neutral-600">
                  {r.plan_name} · {r.cycle} · {formatLocalDateInTz(r.next_billing_date, orgTz)}
                  {r.next_billing_date < todayStr && <span className="text-red-700"> (overdue to invoice)</span>}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1 text-xs">
                  {r.is_trial_conversion && <span className="bg-warning-100 text-warning-800 px-2 py-0.5 rounded-full">Trial converts</span>}
                  {r.applied_term && <span className="bg-primary-100 text-primary-800 px-2 py-0.5 rounded-full">Offer −{money(r.applied_term.amount)}</span>}
                </div>
              </div>
              <div className="text-right font-semibold text-neutral-900 whitespace-nowrap">
                {r.total > 0 ? money(r.total) : 'Free'}
              </div>
            </div>
          ))}
          <div className="p-4 flex justify-between font-semibold text-neutral-900">
            <span>Total ex GST</span>
            <span>{money(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function TeamTab() {
  const [rows, setRows] = useState<BillingManager[] | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    billingService.listManagers()
      .then(r => { setRows(r ?? []); setError(null); })
      .catch(err => setError(errorText(err, 'Failed to load billing team')));
  }, []);
  useEffect(load, [load]);

  const grant = async () => {
    setBusy(true);
    try { await billingService.grantManager(email.trim()); setEmail(''); load(); }
    catch (err) { setError(errorText(err, 'Could not add')); }
    finally { setBusy(false); }
  };
  const revoke = async (m: BillingManager) => {
    if (!confirm(`Remove billing access from ${m.username}?`)) return;
    try { await billingService.revokeManager(m.id); load(); }
    catch (err) { setError(errorText(err, 'Could not remove')); }
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-neutral-600 mb-4">
        Billing managers can onboard customers, run trials and offers, and chase invoices across every organisation.
        They can't see farm data, manage users, or change the Xero connection.
        Only members of our own organisation can be added.
      </p>
      <ErrorBanner message={error} />
      <div className="flex gap-2 mb-4">
        <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Their account email" aria-label="Email to add" className={inputClass} />
        <Button variant="primary" onClick={grant} disabled={busy || !email.trim()}>Add</Button>
      </div>
      {rows === null ? (
        <Loader2 className="h-6 w-6 text-primary-600 animate-spin mx-auto my-6" />
      ) : rows.length === 0 ? (
        <p className="text-neutral-500">No billing managers yet.</p>
      ) : (
        <ul className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100">
          {rows.map(m => (
            <li key={m.id} className="p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-neutral-900">{m.username}</p>
                <p className="text-sm text-neutral-500 break-all">{m.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => revoke(m)} aria-label={`Remove ${m.username}`}>
                <X className="h-4 w-4 text-error-600" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
